import { useState, useEffect, useMemo } from 'react';
import type { MockInspection } from '@/mocks/qms';
import { calcSampleQty } from '@/mocks/qms';
import { getAllInspectionChecklists } from '@/api/inspectionchecklist.api';
import { getAllOperators } from '@/api/operators.api';
import { getAllItems } from '@/api/item.api';
import { getAllProductionOrders, getProductionOrderById } from '@/api/productionOrder.api';
import { getAllWorkOrders } from '@/api/workorder.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import SearchableSelect, { type SearchableOption } from '@/components/base/SearchableSelect';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inspection: MockInspection) => void;
}

interface ChecklistState {
  id: string;
  name: string;
  applicableTo: string;
  samplingPlan: 'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL';
  isActive: boolean;
  parameters: string[];
}

interface OperatorState {
  id: string;
  name: string;
  employeeCode: string;
  skill: string;
  isActive: boolean;
}

interface ItemState {
  id: string;
  code: string;
}

interface ProductionOrderState {
  id: string;
  poNumber: string;
  productId: string;
  productName: string;
  plannedQty: number;
  completedQty: number;
  status: string;
  unit: string;
  warehouseId?: string;
  routingId?: string | null;
  routingStages?: any[];
  isVariant?: boolean;
  variantName?: string | null;
}

interface WorkOrderState {
  id: string;
  productionOrderId: string;
  stageNumber: number;
  stageName: string;
  plannedQty: number;
  status: string;
  woNumber: string;
  checklistId?: string | null;
  checklistName?: string | null;
  qcRequired?: boolean;
  routingStages?: any[];
}

export default function NewInprocessSlideOver({ isOpen, onClose, onSave }: Props) {
  const selectedWarehouseId = useWarehouseStore((state) => state.selectedWarehouseId);
  const [poId, setPoId] = useState('');
  const [woId, setWoId] = useState('');
  const [checklistId, setChecklistId] = useState('');
  const [samplingPlan, setSamplingPlan] = useState<'ALL' | 'RANDOM_10' | 'RANDOM_20' | 'AQL'>('ALL');
  const [sampleQty, setSampleQty] = useState(0);
  const [inspectorId, setInspectorId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [totalQty, setTotalQty] = useState(0);
  const [unit, setUnit] = useState('');

  // Master data states
  const [productionOrders, setProductionOrders] = useState<ProductionOrderState[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrderState[]>([]);
  const [checklists, setChecklists] = useState<ChecklistState[]>([]);
  const [operators, setOperators] = useState<OperatorState[]>([]);
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    async function loadData() {
      try {
        setLoading(true);
        const [poRes, woRes, checklistsRes, operatorsRes, itemsRes] = await Promise.all([
          getAllProductionOrders({ warehouseId: selectedWarehouseId || undefined }),
          getAllWorkOrders(selectedWarehouseId || undefined),
          getAllInspectionChecklists(),
          getAllOperators(),
          getAllItems(selectedWarehouseId || undefined),
        ]);
        if (!active) return;

        if (poRes.success && poRes.data) {
          const mappedPOs: ProductionOrderState[] = poRes.data.map((p: any) => ({
            id: p.id,
            poNumber: p.po_number,
            productId: p.item_id,
            productName: p.item_name || 'Unknown Product',
            plannedQty: Number(p.planned_qty),
            completedQty: Number(p.completed_qty),
            status: p.status,
            unit: 'Pcs',
            warehouseId: p.warehouse_id,
            routingId: p.routing_id || p.routingId || null,
            routingStages: Array.isArray(p.routing_stages)
              ? p.routing_stages
              : typeof p.routing_stages === 'string'
              ? (() => { try { return JSON.parse(p.routing_stages); } catch { return []; } })()
              : Array.isArray(p.stages)
              ? p.stages
              : [],
            isVariant: false,
            variantName: null,
          }));
          setProductionOrders(mappedPOs);
        }

        if (woRes.success && woRes.data) {
          const mappedWOs: WorkOrderState[] = woRes.data.map((w: any) => ({
            id: w.id,
            productionOrderId: w.production_order_id,
            stageNumber: w.stage_number,
            stageName: w.stage_name,
            plannedQty: Number(w.planned_qty),
            status: w.status,
            woNumber: w.wo_number,
            checklistId: w.checklist_id || w.qc_checklist_id || null,
            checklistName: w.checklist_name || w.qc_checklist_name || null,
            qcRequired: Boolean(w.qc_required),
            routingStages: Array.isArray(w.routing_stages)
              ? w.routing_stages
              : typeof w.routing_stages === 'string'
              ? (() => { try { return JSON.parse(w.routing_stages); } catch { return []; } })()
              : [],
          }));
          setWorkOrders(mappedWOs);
        }

        if (checklistsRes.success && checklistsRes.data) {
          const mappedChecklists: ChecklistState[] = checklistsRes.data.map((c) => ({
            id: c.id,
            name: c.name,
            applicableTo: c.applicable_to,
            samplingPlan: c.sampling_plan as ChecklistState['samplingPlan'],
            isActive: c.is_active,
            parameters: c.parameter_ids || [],
          }));
          setChecklists(mappedChecklists);
        }

        if (operatorsRes.success && operatorsRes.data) {
          const mappedOperators: OperatorState[] = operatorsRes.data.map((o) => ({
            id: o.id,
            name: o.name,
            employeeCode: o.employee_code || '',
            skill: o.skill,
            isActive: o.is_active,
          }));
          setOperators(mappedOperators);
        }

        if (itemsRes.success && itemsRes.data) {
          const mappedItems: ItemState[] = itemsRes.data.map((i) => ({
            id: i.id,
            code: i.code || '',
          }));
          setItems(mappedItems);
        }
      } catch (err) {
        console.error('Failed to load master data in NewInprocessSlideOver:', err);
      } finally {
        if (active) setLoading(false);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, [isOpen, selectedWarehouseId]);

  const inProgressPOs = useMemo(() => {
    const filtered = productionOrders.filter(
      (po) =>
        !po.status ||
        ['IN_PROGRESS', 'PLANNED', 'RELEASED', 'PENDING', 'CONFIRMED', 'DRAFT'].includes(
          po.status.toUpperCase()
        )
    );
    return filtered.length > 0 ? filtered : productionOrders;
  }, [productionOrders]);

  const filteredWOs = useMemo(() => {
    if (!poId) return [];
    return workOrders.filter(
      (wo) => wo.productionOrderId === poId
    );
  }, [poId, workOrders]);

  const [poRoutingStages, setPoRoutingStages] = useState<any[]>([]);

  // Fetch full routing stages for the selected PO if not already in state
  useEffect(() => {
    if (!poId) {
      setPoRoutingStages([]);
      return;
    }
    const selectedPO = productionOrders.find((p) => p.id === poId);
    if (selectedPO?.routingStages && selectedPO.routingStages.length > 0) {
      setPoRoutingStages(selectedPO.routingStages);
    } else {
      getProductionOrderById(poId)
        .then((res) => {
          if (res.success && res.data) {
            const stages = (res.data as any).routing_stages || (res.data as any).stages || [];
            setPoRoutingStages(Array.isArray(stages) ? stages : []);
          }
        })
        .catch(() => {});
    }
  }, [poId, productionOrders]);

  // Extract checklist IDs and names defined in Routing for this production order
  const routingChecklistInfo = useMemo(() => {
    const ids = new Set<string>();
    const names = new Set<string>();

    if (Array.isArray(poRoutingStages)) {
      poRoutingStages.forEach((st: any) => {
        const cId = st.qcChecklistId || st.checklist_id || st.checklistId;
        const cName = st.qcChecklistName || st.checklist_name;
        if (cId) {
          const clean = String(cId).replace('checklist:', '').replace('param:', '').trim();
          ids.add(clean);
        }
        if (cName) names.add(String(cName).trim().toLowerCase());
      });
    }

    filteredWOs.forEach((wo) => {
      if (wo.checklistId) ids.add(String(wo.checklistId).trim());
      if (wo.checklistName) names.add(String(wo.checklistName).trim().toLowerCase());
      if (Array.isArray(wo.routingStages)) {
        wo.routingStages.forEach((st: any) => {
          const cId = st.qcChecklistId || st.checklist_id || st.checklistId;
          const cName = st.qcChecklistName || st.checklist_name;
          if (cId) {
            const clean = String(cId).replace('checklist:', '').replace('param:', '').trim();
            ids.add(clean);
          }
          if (cName) names.add(String(cName).trim().toLowerCase());
        });
      }
    });

    return { ids, names };
  }, [poRoutingStages, filteredWOs]);

  // Only show inspection checklists that are used in Routing for this production order
  const inprocessChecklists = useMemo(() => {
    if (!poId) {
      return [];
    }

    const { ids, names } = routingChecklistInfo;

    const filtered = checklists.filter((c) => {
      if (c.isActive === false) return false;
      return ids.has(String(c.id).trim()) || names.has(c.name.trim().toLowerCase());
    });

    return filtered;
  }, [poId, checklists, routingChecklistInfo]);

  const qcInspectors = useMemo(() => {
    const filtered = operators.filter(
      (o) =>
        (!o.skill ||
          o.skill.toUpperCase() === 'QC_INSPECTOR' ||
          o.skill.toUpperCase().includes('QC') ||
          o.skill.toUpperCase().includes('INSPECTOR')) &&
        o.isActive !== false
    );
    return filtered.length > 0 ? filtered : operators;
  }, [operators]);

  const poSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Production Order --' },
    ...inProgressPOs.map((po) => ({
      value: po.id,
      label: `${po.poNumber} — ${po.productName}`,
      sublabel: `${po.completedQty}/${po.plannedQty} ${po.unit}`
    }))
  ], [inProgressPOs]);

  const woSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: poId ? '-- Select Work Order --' : '-- Choose PO first --' },
    ...filteredWOs.map((wo) => ({
      value: wo.id,
      label: `Stage ${wo.stageNumber}: ${wo.stageName}`,
      sublabel: `${wo.plannedQty} ${poId ? productionOrders.find((p) => p.id === poId)?.unit || '' : ''}`
    }))
  ], [filteredWOs, poId, productionOrders]);

  const checklistSelectOptions: SearchableOption[] = useMemo(() => {
    if (!poId) {
      return [{ value: '', label: '-- Choose Production Order first --' }];
    }
    if (inprocessChecklists.length === 0) {
      return [
        {
          value: '',
          label: 'No inspection checklist configured in Routing for this order',
        },
      ];
    }
    return [
      { value: '', label: '-- Select Checklist from Routing --' },
      ...inprocessChecklists.map((c) => ({
        value: c.id,
        label: c.name,
        sublabel: `${c.parameters.length} params`,
      })),
    ];
  }, [poId, inprocessChecklists]);

  const samplePlanSelectOptions: SearchableOption[] = useMemo(() => [
    { value: 'ALL', label: '100% (All)' },
    { value: 'RANDOM_10', label: 'Random 10%' },
    { value: 'RANDOM_20', label: 'Random 20%' },
    { value: 'AQL', label: 'AQL' }
  ], []);

  const inspectorSelectOptions: SearchableOption[] = useMemo(() => [
    { value: '', label: '-- Select Inspector --' },
    ...qcInspectors.map((o) => ({
      value: o.id,
      label: o.name,
      sublabel: o.employeeCode
    }))
  ], [qcInspectors]);

  useEffect(() => {
    if (!poId) {
      setWoId('');
      setTotalQty(0);
      setUnit('');
      return;
    }
    const po = productionOrders.find((p) => p.id === poId);
    if (po) {
      setUnit(po.unit);
      // Auto-select first stage if woId is empty
      if (filteredWOs.length > 0 && (!woId || !filteredWOs.some((w) => w.id === woId))) {
        setWoId(filteredWOs[0].id);
      }
    }
  }, [poId, productionOrders, filteredWOs, woId]);

  useEffect(() => {
    if (!woId) {
      if (poId) {
        const po = productionOrders.find((p) => p.id === poId);
        if (po) setTotalQty(po.plannedQty);
      }
      return;
    }
    const wo = filteredWOs.find((w) => w.id === woId);
    if (wo) {
      setTotalQty(wo.plannedQty);
    }
  }, [woId, poId, filteredWOs, productionOrders]);

  // Auto-select or sync checklist based on stage and PO routing
  useEffect(() => {
    if (!poId) {
      setChecklistId('');
      return;
    }

    if (woId) {
      const wo = filteredWOs.find((w) => w.id === woId);
      let matchedChecklistId = '';

      if (wo?.checklistId) {
        matchedChecklistId = String(wo.checklistId).trim();
      } else if (wo?.checklistName) {
        const found = inprocessChecklists.find(
          (c) => c.name.trim().toLowerCase() === wo.checklistName?.trim().toLowerCase()
        );
        if (found) matchedChecklistId = found.id;
      }

      if (!matchedChecklistId && Array.isArray(poRoutingStages)) {
        const matchedStage = poRoutingStages.find((st: any, idx: number) => {
          const seq = Number(st.sequence || st.stageNumber || st.sequence_no || idx + 1);
          return (
            seq === Number(wo?.stageNumber) ||
            (st.stageName && wo?.stageName && st.stageName.toLowerCase().trim() === wo.stageName.toLowerCase().trim()) ||
            (st.operationName && wo?.stageName && st.operationName.toLowerCase().trim() === wo.stageName.toLowerCase().trim())
          );
        });
        if (matchedStage) {
          const rawCId = matchedStage.qcChecklistId || matchedStage.checklist_id || matchedStage.checklistId;
          const cleanId = rawCId ? String(rawCId).replace('checklist:', '').replace('param:', '').trim() : '';
          const stageCName = matchedStage.qcChecklistName || matchedStage.checklist_name;

          const found = inprocessChecklists.find(
            (c) =>
              (cleanId && String(c.id).trim() === cleanId) ||
              (stageCName && c.name.trim().toLowerCase() === String(stageCName).trim().toLowerCase())
          );
          if (found) matchedChecklistId = found.id;
        }
      }

      if (matchedChecklistId && inprocessChecklists.some((c) => c.id === matchedChecklistId)) {
        setChecklistId(matchedChecklistId);
        return;
      }
    }

    if (inprocessChecklists.length === 1) {
      setChecklistId(inprocessChecklists[0].id);
    } else if (checklistId && !inprocessChecklists.some((c) => c.id === checklistId)) {
      setChecklistId('');
    }
  }, [poId, woId, inprocessChecklists, filteredWOs, poRoutingStages]);

  useEffect(() => {
    if (!checklistId) return;
    const cl = checklists.find((c) => c.id === checklistId);
    if (cl) {
      setSamplingPlan(cl.samplingPlan);
    }
  }, [checklistId, checklists]);

  useEffect(() => {
    setSampleQty(calcSampleQty(samplingPlan, totalQty));
  }, [samplingPlan, totalQty]);

  const handleSave = () => {
    if (!poId || !checklistId || !inspectorId) return;
    const po = productionOrders.find((p) => p.id === poId)!;
    const wo = filteredWOs.find((w) => w.id === woId) || filteredWOs[0] || {
      id: `wo-default`,
      stageName: 'Production Stage 1',
      woNumber: 'WO-STAGE-1',
      stageNumber: 1,
      plannedQty: po.plannedQty,
    };
    const checklist = checklists.find((c) => c.id === checklistId)!;
    const inspector = operators.find((o) => o.id === inspectorId)!;
    const item = items.find((i) => i.id === po.productId);

    const nextNum = productionOrders.length + 5;
    const newInspection: MockInspection = {
      id: `insp-${Date.now()}`,
      inspectionNumber: `INSP-2024-00${nextNum}`,
      type: 'IN_PROCESS',
      status: 'PENDING',
      triggeredBy: 'MANUAL',
      sourceType: 'PRODUCTION_ORDER',
      sourceId: po.id,
      sourceNumber: po.poNumber,
      itemId: po.productId,
      itemName: po.productName,
      itemCode: item?.code || null,
      isVariant: po.isVariant ?? false,
      variantName: po.variantName || null,
      checklistId: checklist.id,
      checklistName: checklist.name,
      samplingPlan,
      batchNumber: null,
      lotNumber: null,
      totalQty,
      sampleQty,
      passedQty: 0,
      failedQty: 0,
      unit: unit || po.unit,
      inspectorId: inspector.id,
      inspectorName: inspector.name,
      scheduledDate,
      completedDate: null,
      warehouseId: po.warehouseId || selectedWarehouseId || null,
      notes: `Stage: ${wo.stageName} (WO: ${wo.woNumber})`,
      createdAt: new Date().toISOString(),
    };
    onSave(newInspection);
    resetForm();
  };

  const resetForm = () => {
    setPoId('');
    setWoId('');
    setChecklistId('');
    setSamplingPlan('ALL');
    setSampleQty(0);
    setInspectorId('');
    setScheduledDate(new Date().toISOString().split('T')[0]);
    setTotalQty(0);
    setUnit('');
  };

  if (!isOpen) return null;

  const isValid = Boolean(poId && checklistId && inspectorId);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full shadow-xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New In-Process Inspection</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Production Order <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={poId}
              onChange={(val) => { setPoId(val); setWoId(''); }}
              options={poSelectOptions}
              placeholder="-- Select Production Order --"
              searchPlaceholder="Search production order..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stage / Work Order <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={woId}
              onChange={(val) => setWoId(val)}
              options={woSelectOptions}
              disabled={!poId}
              placeholder={poId ? '-- Select Work Order --' : '-- Choose PO first --'}
              searchPlaceholder="Search work order stage..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Checklist <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={checklistId}
              onChange={(val) => setChecklistId(val)}
              options={checklistSelectOptions}
              placeholder="-- Select Checklist --"
              searchPlaceholder="Search checklist..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Plan</label>
              <SearchableSelect
                value={samplingPlan}
                onChange={(val) => setSamplingPlan(val as typeof samplingPlan)}
                options={samplePlanSelectOptions}
                placeholder="Select Sample Plan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sample Qty</label>
              <input
                type="number"
                value={sampleQty || ''}
                onChange={(e) => setSampleQty(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              />
              <p className="text-xs text-gray-400 mt-1">Auto-calculated from plan</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inspector <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              value={inspectorId}
              onChange={(val) => setInspectorId(val)}
              options={inspectorSelectOptions}
              placeholder="-- Select Inspector --"
              searchPlaceholder="Search inspector..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date</label>
            <input
              type="date"
              value={scheduledDate}
              onChange={(e) => setScheduledDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className={`px-4 py-2 rounded-md text-sm font-medium text-white whitespace-nowrap ${
              isValid ? 'bg-sky-600 hover:bg-sky-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Create Inspection
          </button>
        </div>
      </div>
    </div>
  );
}